import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@apollo/client";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { Snackbar, Alert } from "@mui/material";
import Header from "../../components/Header";
import Load from "../../components/Load";
import UpdateAvatarModal from "../../components/UpdateAvatarModal";
import {
  buscarAtletaPorId,
  atualizarAtleta,
  Atleta,
  AtualizarAtletaInput,
} from "../../services/atletaService";
import { atualizarUsuario, UsuarioInput } from "../../services/usuarioService";
import { atualizarImagemUsuario } from "../../services/userService";
import {
  LISTAR_CIDADES,
  Cidade,
  LISTAR_ESTADOS,
  Estado,
} from "../../services/localizacaoService";
import { dispatchGlobalError } from "../../context/ApiErrorContext";
import { validateCpf, onlyDigits } from "../../utils/cpf";
import ChangePasswordDialog from "../../components/admin/athletes/ChangePasswordDialog";

const schema = z.object({
  nome: z.string().nonempty("Nome é obrigatório"),
  email: z.string().email("Email inválido"),
  imagemUrl: z.string().optional(),
  role: z.string().nonempty("Role é obrigatório"),
  ativo: z.boolean(),
  cpf: z
    .string()
    .optional()
    .refine((val) => !val || validateCpf(val), { message: "CPF inválido" }),
  dataNascimento: z.string().optional(),
  telefone: z.string().optional(),
  estadoId: z.string().optional(),
  cidadeId: z.string().optional(),
  sobre: z.string().optional(),
});

export type EditAthleteFormValues = z.infer<typeof schema>;

const EditAthlete: React.FC = () => {
  const { id: paramId } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const queryId = new URLSearchParams(location.search).get("id");
  const id = paramId || queryId || "";

  const [athlete, setAthlete] = useState<Atleta | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [avatarModalOpen, setAvatarModalOpen] = useState(false);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string }>({ open: false, message: '' });

  const { data: cidadesData } = useQuery<{ cidades: Cidade[] }>(LISTAR_CIDADES);
  const { data: estadosData } = useQuery<{ estados: Estado[] }>(LISTAR_ESTADOS);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<EditAthleteFormValues>({ resolver: zodResolver(schema) });

  const load = async () => {
    if (!id) return;
    setLoading(true);
    setError(false);
    try {
      const data = await buscarAtletaPorId(id);
      setAthlete(data);
      reset({
        nome: data.usuario?.nome || "",
        email: data.usuario?.email || "",
        imagemUrl: data.usuario?.imagemUrl || "",
        role: data.usuario?.role || "ATLETA",
        ativo:
          typeof data.usuario?.ativo === "boolean"
            ? data.usuario.ativo
            : !!data.usuario?.ativo,
        cpf: data.cpf || "",
        dataNascimento: data.dataNascimento || "",
        telefone: data.telefone || "",
        estadoId: data.cidade?.estado?.id || "",
        cidadeId: data.cidade?.id || "",
        sobre: data.sobre || "",
      });
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleAvatarSave = async (file: File) => {
    if (!athlete?.usuario) return;
    try {
      const url = await atualizarImagemUsuario(athlete.usuario.id, file);
      setValue("imagemUrl", url);
    } catch (e) {
      dispatchGlobalError("Erro ao atualizar imagem");
    }
  };

  const onSubmit = async (values: EditAthleteFormValues) => {
    if (!athlete?.usuario) return;
    try {
      const usuarioInput: UsuarioInput = {
        nome: values.nome,
        email: values.email,
        role: values.role,
        ativo: values.ativo,
      };
      await atualizarUsuario(athlete.usuario.id, usuarioInput);
      const atletaInput: AtualizarAtletaInput = {
        cpf: values.cpf ? onlyDigits(values.cpf) : values.cpf,
        dataNascimento: values.dataNascimento,
        telefone: values.telefone,
        cidadeId: values.cidadeId,
        sobre: values.sobre,
      };
      await atualizarAtleta(id, atletaInput);
      alert("Atleta atualizado com sucesso.");
      navigate("/admin/atletas");
    } catch (e) {
      dispatchGlobalError("Erro ao salvar atleta");
    }
  };

  const image = watch("imagemUrl");
  const selectedEstadoId = watch("estadoId");

  const cidadesOptions = useMemo<Cidade[]>(() => {
    const allCities = cidadesData?.cidades || [];
    if (!selectedEstadoId) {
      return allCities;
    }
    return allCities.filter((cidade) => cidade.estado?.id === selectedEstadoId);
  }, [cidadesData, selectedEstadoId]);

  if (loading) return <Load />;
  if (error)
    return (
      <div className="alert alert-danger">
        Erro ao carregar atleta.
        <button className="btn btn-link" onClick={load}>
          Tentar novamente
        </button>
      </div>
    );
  if (!athlete) return null;

  const roleOptions = ["ADMIN", "ATLETA"];

  return (
    <div className="main-wrapper">
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left">
            <div className="middle-wrap">
              <div className="card w-100 border-0 bg-white shadow-xs p-0 mb-4">
                <div
                  id="header-athletes"
                  className="card-title d-flex flex-row pt-3"
                >
                  <div className="col-1">
                    <button
                      type="button"
                      className="btn  text-primary p-0 d-inline-block mt-2 me-2 ms-3"
                      onClick={() => window.history.back()}
                    >
                      <i className="ti-arrow-left font-sm"></i>
                    </button>
                  </div>
                  <div className="col-8 d-flex align-items-center">
                    <h4 className="font-xs text-primary fw-600  mb-0 mt-2 flex-grow-1">
                      Editar Atleta
                    </h4>
                  </div>
                </div>
                <div className="card-body p-4 w-100 border-0 d-flex rounded-3 align-items-center">
                  <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="p-4 shadow-xs"
                  >
                    <div className="row g-4">
                      <div className="col-12">
                        <div className="mb-3 text-center">
                          <div>
                            <div
                              style={{
                                position: "relative",
                                display: "inline-block",
                              }}
                            >
                              <img
                                src={image || "/assets/images/user.png"}
                                alt="avatar"
                                className="rounded-circle"
                                style={{
                                  width: 120,
                                  height: 120,
                                  objectFit: "cover",
                                }}
                              />
                              <button
                                type="button"
                                className="btn btn-light btn-sm avatar-edit-btn"
                                onClick={() => setAvatarModalOpen(true)}
                                title="Alterar imagem"
                                style={{
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                  transform: "translate(-50%, -50%)",
                                  borderRadius: "50%",
                                  width: 40,
                                  height: 40,
                                  display: "none",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  zIndex: 2,
                                }}
                              >
                                <i className="bi bi-camera font-sm"></i>
                              </button>
                              <style>
                                {`
                                  .avatar-edit-btn {
                                    display: none !important;
                                  }
                                  div:hover > .avatar-edit-btn {
                                    display: flex !important;
                                  }
                                `}
                              </style>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 d-flex justify-content-between align-items-center mb-3">
                        <div className="form-check form-switch">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            id="ativo"
                            {...register("ativo")}
                            disabled={isSubmitting}
                          />
                          <label className="form-check-label ps-1" htmlFor="ativo">
                            Ativo
                          </label>
                        </div>
                        {athlete?.usuario?.id && (
                          <button
                            type="button"
                            className="btn btn-outline-warning btn-sm"
                            onClick={() => setPasswordDialogOpen(true)}
                          >
                            <i className="bi bi-key-fill me-1"></i>
                            Alterar Senha
                          </button>
                        )}
                      </div>
                      <div className="col-12">
                        <div className="mb-3">
                          <label className="form-label">Nome completo*</label>
                          <input
                            className="form-control"
                            {...register("nome")}
                            disabled={isSubmitting}
                          />
                          {errors.nome && (
                            <div className="text-danger small">
                              {errors.nome.message}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="mb-3 col-lg-6">
                        <label className="form-label">E-mail*</label>
                        <input
                          type="email"
                          className="form-control"
                          {...register("email")}
                          disabled={isSubmitting}
                        />
                        {errors.email && (
                          <div className="text-danger small">
                            {errors.email.message}
                          </div>
                        )}
                      </div>
                      <div className="mb-3 col-lg-6">
                        <label className="form-label">Telefone</label>
                        <input
                          className="form-control"
                          {...register("telefone")}
                          disabled={isSubmitting}
                        />
                      </div>
                      <div className="mb-3 col-lg-6">
                        <label className="form-label">CPF</label>
                        <input
                          className="form-control"
                          {...register("cpf")}
                          disabled={isSubmitting}
                        />
                        {errors.cpf && (
                          <div className="text-danger small">
                            {errors.cpf.message}
                          </div>
                        )}
                      </div>

                      <div className="mb-3 col-lg-6">
                        <label className="form-label">Estado</label>
                        <Controller
                          name="estadoId"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              size="medium"
                              options={estadosData?.estados || []}
                              getOptionLabel={(option: Estado) => option.nome}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              value={
                                (estadosData?.estados || []).find(
                                  (estado) => estado.id === field.value
                                ) || null
                              }
                              onChange={(_, newValue) => {
                                field.onChange(newValue ? newValue.id : "");
                                setValue("cidadeId", "");
                              }}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Selecione o estado"
                                />
                              )}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                      </div>

                      <div className="mb-3 col-lg-6">
                        <label className="form-label">Cidade</label>
                        <Controller
                          name="cidadeId"
                          control={control}
                          render={({ field }) => (
                            <Autocomplete
                              options={cidadesOptions}
                              getOptionLabel={(option: Cidade) => option.nome}
                              isOptionEqualToValue={(option, value) =>
                                option.id === value.id
                              }
                              value={
                                cidadesOptions.find(
                                  (cidade) => cidade.id === field.value
                                ) || null
                              }
                              onChange={(_, newValue) =>
                                field.onChange(newValue ? newValue.id : "")
                              }
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  placeholder="Selecione a cidade"
                                />
                              )}
                              disabled={isSubmitting}
                            />
                          )}
                        />
                      </div>

                      <div className="mb-3 col-lg-6">
                        <label className="form-label">Data de Nascimento</label>
                        <input
                          type="date"
                          className="form-control"
                          {...register("dataNascimento")}
                          disabled={isSubmitting}
                        />
                      </div>

                      <div className="mb-3 col-lg-12">
                        <label className="form-label">Sobre</label>
                        <textarea
                          rows={4}
                          {...register("sobre")}
                          disabled={isSubmitting}
                          id="sobre"
                          name="sobre"
                          className="form-control"
                          maxLength={140}
                          style={{ resize: "none", minHeight: "100px" }}
                        />
                      </div>
                    </div>
                    <div className="d-flex justify-content-end gap-2 mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => navigate("/admin/atletas")}
                        disabled={isSubmitting}
                      >
                        Cancelar
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                      >
                        Salvar
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <UpdateAvatarModal
        open={avatarModalOpen}
        onClose={() => setAvatarModalOpen(false)}
        currentImage={image}
        onSave={handleAvatarSave}
      />
      {athlete?.usuario && (
        <ChangePasswordDialog
          open={passwordDialogOpen}
          onClose={() => {
            setPasswordDialogOpen(false);
            setSnackbar({ open: true, message: 'Senha alterada com sucesso!' });
          }}
          userId={athlete.usuario.id}
          userName={athlete.usuario.nome}
        />
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ open: false, message: '' })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ open: false, message: '' })} severity="success" sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default EditAthlete;
